import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiConstants } from '../constants/api-constants';

export type PickupStatus = 'waiting' | 'collected';
export type PickupScanAction = 'parent_wait' | 'student_collect' | 'bus_arrive' | 'buses_ready';

export interface PickupPerson {
  id: string;
  firstNameAr?: string;
  lastNameAr?: string;
  firstNameEn?: string;
  lastNameEn?: string;
  studentCode?: string;
  class?: { id: string; name: string } | null;
}

export interface PickupBusInfo {
  id: string;
  name?: string;
  number?: string;
  plateNumber?: string;
  driverName?: string;
}

export interface PickupItem {
  id: string;
  date: string;
  status: PickupStatus;
  waitingAt: number | null;
  collectedAt: number | null;
  class: { id: string; name: string } | null;
  student: PickupPerson | null;
  parent: PickupPerson | null;
}

export interface PickupBusItem {
  id: string;
  date: string;
  waitingAt: number | null;
  bus: PickupBusInfo | null;
}

export interface PickupBoardResult {
  date: string;
  waiting: PickupItem[];
  collected: PickupItem[];
  buses: PickupBusItem[];
  allBusesReady?: boolean;
  allBusesReadyAt?: number | null;
  items: PickupItem[];
}

export interface PickupScanResult {
  ok: boolean;
  action: PickupScanAction;
  date: string;
  alreadyScanned: boolean;
  reason: string | null;
  counts?: {
    added: number;
    alreadyWaiting?: number;
    alreadyCollected?: number;
    total?: number;
    alreadyReady?: number;
  };
  parent?: PickupPerson | null;
  student?: PickupPerson | null;
  bus?: PickupBusInfo | null;
  buses?: PickupBusItem[];
  items: PickupItem[];
}

export interface PickupBusesReadyResult {
  ok: boolean;
  action: 'buses_ready';
  date: string;
  counts: {
    total: number;
    added: number;
    alreadyReady: number;
  };
  buses: PickupBusItem[];
}

@Injectable({ providedIn: 'root' })
export class PickupService {
  private get url() {
    return ApiConstants.URL_PICKUP;
  }

  constructor(private http: HttpClient) {}

  async getToday(classId?: string): Promise<PickupBoardResult> {
    const params: Record<string, string> = {};
    if (classId) {
      params.class = classId;
    }
    const response = await firstValueFrom(
      this.http.get<any>(`${this.url}/today`, { params }),
    );
    return response.data;
  }

  async scan(qrCode: string, classId?: string): Promise<PickupScanResult> {
    const body: Record<string, string> = { qrCode };
    if (classId) {
      body.classId = classId;
    }
    const response = await firstValueFrom(
      this.http.post<any>(`${this.url}/scan`, body),
    );
    return response.data;
  }

  async markAllBusesReady(): Promise<PickupBusesReadyResult> {
    const response = await firstValueFrom(
      this.http.post<any>(`${this.url}/buses-ready`, {}),
    );
    return response.data;
  }

  async collectWaitingStudent(studentId: string): Promise<PickupScanResult> {
    const response = await firstValueFrom(
      this.http.post<any>(`${this.url}/collect`, { studentId }),
    );
    return response.data;
  }
}
